'use client'

import { BuilderBlocks } from '@builder.io/react'
import { type CSSProperties, type ReactNode } from 'react'

import { useBreakpoints } from '@src/utils/breakpoints.util'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

import { VideoPlayer } from '../VideoPlayer/VideoPlayer'
import type {
  ContentSides,
  ContentSizes,
  ImagePositions,
  ImageSizes,
  ImageTransformOrigins,
  MediaTypes,
  PaddingYVariants,
} from './SplitContent.builder'

export interface SplitContentProps {
  attributes?: any
  backgroundColor?: string
  backgroundImageUrl?: string
  backgroundPosition?: (typeof ImagePositions)[number]
  backgroundSize?: (typeof ImageSizes)[number]
  brightcoveVideoId?: string
  builderBlock?: any
  children?: ReactNode
  contentSide?: (typeof ContentSides)[number]
  contentSize?: (typeof ContentSizes)[number]
  mediaType?: (typeof MediaTypes)[number]
  mediaBlocks?: ReactNode
  mobileBackgroundColor?: SplitContentProps['backgroundColor']
  mobileBackgroundHidden?: boolean
  mobileBackgroundImageUrl?: SplitContentProps['backgroundImageUrl']
  mobileBackgroundPosition?: SplitContentProps['backgroundPosition']
  mobileBackgroundSize?: SplitContentProps['backgroundSize']
  mobileContentSide?: SplitContentProps['contentSide']
  mobileFlip?: boolean
  mobilePhotoHeight?: number
  mobilePhotoHidden?: boolean
  mobilePhotoPosition?: SplitContentProps['photoPosition']
  mobilePhotoScale?: SplitContentProps['photoScale']
  mobilePhotoSize?: SplitContentProps['photoSize']
  mobilePhotoTransformOriginY?: (typeof ImageTransformOrigins)[number]
  mobilePhotoUrl?: SplitContentProps['photoUrl']
  mobileRoundedNone?: boolean
  photoPosition?: (typeof ImagePositions)[number]
  photoScale?: number
  photoSize?: (typeof ImageSizes)[number]
  photoTransformOriginY?: (typeof ImageTransformOrigins)[number]
  photoUrl?: string
  playAsPopup?: boolean
  roundedNone?: boolean
  paddingYVariant?: (typeof PaddingYVariants)[number]
  videoSEO?: SplitContentVideoSEOData
}

export interface SplitContentVideoSEOData {
  name?: string
  description?: string
  thumbnailUrl?: string
  uploadDate?: string
  duration?: string
  embedUrl?: string
}

export function SplitContent(props: SplitContentProps) {
  const { isMobile, isTablet } = useBreakpoints()
  const { edit } = getBuilderPreviewMode()

  const contentSide = getProp(props.contentSide, props.mobileContentSide) ?? 'left'
  const backgroundColor = getProp(props.backgroundColor, props.mobileBackgroundColor)
  const backgroundImageUrl = getProp(props.backgroundImageUrl, props.mobileBackgroundImageUrl)
  const backgroundSize = getProp(props.backgroundSize, props.mobileBackgroundSize)
  const backgroundPosition = getProp(props.backgroundPosition, props.mobileBackgroundPosition)
  const backgroundHidden = getProp(undefined, props.mobileBackgroundHidden) ?? false
  const photoUrl = getProp(props.photoUrl, props.mobilePhotoUrl)
  const photoSize = getProp(props.photoSize, props.mobilePhotoSize)
  const photoPosition = getProp(props.photoPosition, props.mobilePhotoPosition)
  const photoTransformOriginY = getProp(
    props.photoTransformOriginY,
    props.mobilePhotoTransformOriginY
  )
  const photoScale = getProp(props.photoScale, props.mobilePhotoScale)
  const photoHeight = getProp(undefined, props.mobilePhotoHeight)
  const photoHidden = getProp(undefined, props.mobilePhotoHidden)

  const paddingY = props.paddingYVariant ?? 'large'

  const isMediaVideo = props.mediaType === 'video'
  const isMediaPhoto = props.mediaType === 'photo'
  const isMediaBlocks = props.mediaType === 'blocks'

  const seoTemplate = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: props.videoSEO?.name,
    description: props.videoSEO?.description,
    thumbnailUrl: props.videoSEO?.thumbnailUrl,
    uploadDate: props.videoSEO?.uploadDate,
    duration: props.videoSEO?.duration,
    embedUrl: props.videoSEO?.embedUrl,
  }

  function getProp<T>(prop: T, mobileProp: T) {
    return isMobile || isTablet ? mobileProp || prop : prop
  }

  function getPhoto(side: 'left' | 'right') {
    if (contentSide === side || !isMediaPhoto) return undefined
    return {
      backgroundImage: photoUrl ? `url('${photoUrl}?format=webp')` : undefined,
      backgroundSize: photoSize ?? 'cover',
      backgroundPosition: photoPosition ?? 'center',
      height: photoHeight ?? 'auto',
      transform: `scale(${photoScale ?? 1})`,
      display: photoHidden ? 'none' : undefined,
      transformOrigin: `center ${photoTransformOriginY}`,
    } satisfies CSSProperties
  }

  function getContentSize(side: 'left' | 'right') {
    const forContent = contentSide === side
    const size = forContent ? (props.contentSize ?? 6) : 12 - (props.contentSize ?? 6)
    return { gridColumn: `span ${size}` }
  }

  function renderContent() {
    if (isMediaVideo && props.brightcoveVideoId) {
      return (
        <div className="flex h-full items-center justify-center">
          <VideoPlayer
            videoId={props.brightcoveVideoId}
            autoplay={false}
            playAsPopup={props.playAsPopup}
          />
        </div>
      )
    }
    if (isMediaBlocks) {
      return (
        <BuilderBlocks
          parentElementId={props.builderBlock?.id}
          dataPath="component.options.mediaBlocks"
          blocks={props.mediaBlocks}
          className="size-full"
        />
      )
    }
  }

  return (
    <>
      {edit && isMediaVideo && (
        <div className="pointer-events-none flex items-center justify-center bg-yellow-600/40 p-2 text-center text-body-base text-black opacity-70">
          NOTE: this helper text only shows within the Builder editor - This component has video
          content, please make sure to update the Video SEO data in the component Block Options.
        </div>
      )}
      <div
        data-testid={props?.attributes?.['data-testid']}
        builder-id={props?.attributes?.['builder-id']}
        className={cn(
          'grid w-full auto-rows-min grid-cols-1 bg-slate-400 bg-cover bg-center bg-no-repeat lg:min-h-[448px] lg:grid-cols-12 lg:grid-rows-1',
          {
            'lg:rounded-none': props.roundedNone,
            'lg:rounded-[33px]': !props.roundedNone,
            'rounded-none': props.mobileRoundedNone,
            'rounded-[33px]': !props.mobileRoundedNone,
          },
          props?.attributes?.className
        )}
        style={{
          backgroundColor,
          backgroundSize: backgroundSize ?? 'cover',
          backgroundPosition: backgroundPosition ?? 'center',
          backgroundImage:
            !backgroundHidden && backgroundImageUrl
              ? `url('${backgroundImageUrl}?format=webp')`
              : undefined,
          ...props?.attributes?.style,
        }}
      >
        <section
          style={{ ...getPhoto('left'), ...getContentSize('left') }}
          className={cn(
            'rounded-t-[33px] bg-cover bg-center bg-no-repeat px-6 lg:h-full lg:rounded-none lg:rounded-l-[33px] lg:px-16',
            {
              'rounded-none rounded-b-[33px]': props.mobileFlip,
              'lg:py-0': isMediaVideo && contentSide === 'right',
              'rounded-none': props.mobileRoundedNone,
              'lg:rounded-none': props.roundedNone,
              'py-8 lg:py-28': paddingY === 'large',
              'py-6 lg:py-16': paddingY === 'medium',
            }
          )}
        >
          {contentSide === 'right' && renderContent()}
          {contentSide === 'left' && (
            <BuilderBlocks
              parentElementId={props.builderBlock?.id}
              dataPath={`component.options.children`}
              blocks={props.children}
              className="size-full"
            />
          )}
        </section>
        <section
          style={{ ...getPhoto('right'), ...getContentSize('right') }}
          className={cn(
            'rounded-b-[33px] bg-cover bg-center bg-no-repeat px-6 lg:h-full lg:rounded-none lg:rounded-r-[33px] lg:px-16',
            {
              'order-first rounded-none rounded-t-[33px] lg:order-last': props.mobileFlip,
              'rounded-none': props.mobileRoundedNone,
              'lg:rounded-none': props.roundedNone,
              'py-8 lg:py-28': paddingY === 'large',
              'py-6 lg:py-16': paddingY === 'medium',
              'lg:py-0': isMediaVideo && contentSide === 'left',
            }
          )}
        >
          {contentSide === 'left' && renderContent()}
          {contentSide === 'right' && (
            <BuilderBlocks
              parentElementId={props.builderBlock?.id}
              dataPath={`component.options.children`}
              blocks={props.children}
              className="size-full"
            />
          )}
        </section>
      </div>
      {isMediaVideo && props.videoSEO?.name && (
        <script
          id="videoData"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(seoTemplate, null, 2) }}
        />
      )}
    </>
  )
}
